import { showNotification } from '@helpers/notification-helper';
import { useAppSelector } from '@hooks/app/redux';
import {
  createWorkspace,
  getWorkspaceDetail,
  updateWorkspace,
  uploadWorkspaceImage,
} from '@services/external/provider.api';
import { FormikHelpers } from 'formik';
import React from 'react';
import { useQuery } from 'react-query';
import { useHistory, useRouteMatch } from 'react-router-dom';
import queryClient from '../../../query-client';
import { CreateWorkspaceForm, initialValues } from './CreateWorkspace.schema';

export function useInitialValues(params: { workspaceId?: string }) {
  const { workspaceId } = params;

  const providerId = useAppSelector((state) => state.users.currentProvider?.id)!;

  const { isLoading: workspaceDataLoading, data: workspaceData } = useQuery(
    ['getWorkspace', workspaceId, providerId],
    async () => {
      const { data: axData } = await getWorkspaceDetail(
        providerId as string,
        workspaceId as string,
      );

      return axData.data;
    },
    { enabled: Boolean(workspaceId) },
  );

  const formattedValues = React.useMemo(() => {
    if (!workspaceData) {
      return { ...initialValues };
    }

    const res: CreateWorkspaceForm = {
      ...initialValues,
      workspaceId,
      name: workspaceData.name,
      specialization: workspaceData.expertise
        ? {
            label: workspaceData.expertise.name,
            value: workspaceData.expertise.code,
          }
        : null,
      addressInfo: workspaceData.addressInfo ?? '',
      info: workspaceData.info,
      contactPhone: workspaceData.contactPhone ?? '',
      contactEmail: workspaceData.contactEmail ?? '',
      medicalInsurances: workspaceData.insurancies.map((e: any) => ({
        label: e.name,
        value: e.id,
      })),
      workspaceTags: workspaceData.workspaceTags.map((e) => e.code),
      image: workspaceData.imageUrl ? { preview: workspaceData.imageUrl } : null,
    };

    if (workspaceData.institution === null) {
      res.address = workspaceData.address;
      res.city = workspaceData.city;
      res.postCode = workspaceData.postCode;
      res.country = workspaceData.country;
      res.institutionVariant = 'custom';
    } else {
      res.institution = {
        label: workspaceData.institution.name,
        value: workspaceData.institution.id,
      };
      res.institutionVariant = 'search';
    }

    return res;
  }, [workspaceData, workspaceId]);

  return { initialValues: formattedValues, loading: workspaceDataLoading };
}

export async function onSubmitHandler(
  values: CreateWorkspaceForm,
  helpers: FormikHelpers<CreateWorkspaceForm>,
  extra: { workspaceId?: string; providerId: string },
) {
  const res: Record<any, any> = {
    ...values,
    expertiseCode: values.specialization?.value,
    insurancies: values.medicalInsurances.map((mi) => mi.value),
  };

  delete res.specialization;
  delete res.medicalInsurances;
  delete res.institution;
  delete res.image;

  if (values.institutionVariant === 'search') {
    res.institutionId = values.institution?.value;
    delete res.address;
    delete res.city;
    delete res.postCode;
    delete res.country;
  }

  let workspaceId: string;

  if (extra.workspaceId) {
    if (!values.image) {
      res.imageUrl = null;
    }

    await updateWorkspace(extra.providerId, extra.workspaceId, res);
    workspaceId = extra.workspaceId;
  } else {
    const { data } = await createWorkspace(extra.providerId, res);
    workspaceId = data.data.workspaceId;
  }

  if (values.image && values.image instanceof File) {
    await uploadWorkspaceImage(extra.providerId, workspaceId, values.image);
  }
}

export function useSubmitHandler() {
  const history = useHistory();
  const { params } = useRouteMatch<any>();
  const providerId = useAppSelector((state) => state.users.currentProvider?.id)!;

  const onSubmit = React.useCallback(
    async (values: CreateWorkspaceForm, helpers: FormikHelpers<CreateWorkspaceForm>) => {
      try {
        await onSubmitHandler(values, helpers, { workspaceId: params.workspaceId, providerId });

        await queryClient.invalidateQueries(['searchWorkspaces']);
        helpers.resetForm({ values });

        showNotification({ title: 'Pracovisko úspešne upravené', type: 'SUCCESS', copy: '' });

        setTimeout(() => {
          history.push('/business/profile/departments');
        }, 500);
      } catch (error) {
        showNotification({ title: 'Uloženie pracoviska zlyhalo', type: 'ERROR', copy: '' });
      }
    },
    [providerId, params.workspaceId],
  );

  return { onSubmit };
}
